import { gql as parse } from '@apollo/client/core';
import { Image, Page } from '@react-pdf/renderer';
import React from 'react';
import {
  Body,
  Box,
  Column,
  Columns,
  DynamicComponent,
  Stencil,
  Debug,
  Flex,
  Label,
  Producer,
  SemiTitle,
  Spacer,
  Text,
  Title,
  Wine,
} from '../../pdf-design-system';
import stencil1 from './images/stencil-page1-72dpi.png';
import barcelonaAwardsLogo from './images/barcelonaAwardsIcon.png';

export default function BarcelonaBrandsInfoSheetPage1({ product, ...rest }) {
  return (
    <Page size="A4" style={{ paddingHorizontal: 18 }} {...rest}>
      {/* <Stencil src={stencil1} fixed /> */}

      <Flex
        style={{
          marginTop: 16,
          paddingHorizontal: 5,
          marginBottom: 22,
        }}
      >
        <Box>
          {/* This is the brand */}
          <Title>{product.producer?.name}</Title>
          {/* This is the product name. After reorganizing brands, this will be shorter */}
          <Title variant="smaller">{product.name}</Title>
          <Title variant="smallest">{product.alcohol}% Alc. Vol.</Title>
        </Box>
        <Box></Box>
      </Flex>
      <Box style={{ height: 5, backgroundColor: 'black' }} />

      <Body
        style={{
          marginTop: 33,
        }}
      >
        <Columns>
          <Column
            style={{
              width: 194,
              height: 675,
              alignItems: 'center',
            }}
          >
            <Wine.BottleImage height={490} width={138} trim />
            <Spacer />
            <Box
              style={{
                backgroundColor: 'white',
                height: 170,
                width: '100%',
                marginBottom: 8,
                paddingTop: 13,
              }}
            >
              {product.awards?.map((award) => (
                <AwardItem key={award.award} {...award} />
              ))}
            </Box>
          </Column>

          {/* marginBottom: -10 is a hack to counteract the marginBottom: 10 on each DynamicComponent */}
          <Column
            style={{
              flexGrow: 1,
              paddingLeft: 28,
              width: 352,
              paddingTop: 7,
            }}
          >
            <Text variant="description">
              {product.producer.shortDescription ||
                'Ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem.'}
            </Text>
            <SemiTitle style={{ marginTop: 22 }}>PRODUCTION</SemiTitle>
            <Box
              style={{
                borderLeft: '1px solid black',
                paddingLeft: 4,
                marginTop: 10,
              }}
            >
              <DynamicComponent.Wrapper orientation="horizontal">
                <DynamicComponent.Label orientation="horizontal">
                  Region
                </DynamicComponent.Label>
                <DynamicComponent.Value orientation="horizontal">
                  {product.customSubregion}
                </DynamicComponent.Value>
              </DynamicComponent.Wrapper>
              <Wine.Blend orientation="horizontal" label="Grapes" />
              <Wine.Classification orientation="horizontal" />
              {product.vintage?.toLowerCase() !== 'nv' && (
                <Wine.Vintage orientation="horizontal" />
              )}
              <Wine.BottleSize orientation="horizontal" label="Content" />
            </Box>
            <SemiTitle style={{ marginTop: 22 }}>Tasting notes</SemiTitle>
            <Box
              style={{
                marginTop: 7,
              }}
            >
              <Text style={{ marginBottom: 2 }}>
                <Text style={{ fontWeight: 500 }}>Color:</Text>{' '}
                {product.appearanceDescription ||
                  'Bright pink with reflections of peach color.'}
              </Text>
              <Text style={{ marginBottom: 2 }}>
                <Text style={{ fontWeight: 500 }}>On the Nose:</Text>{' '}
                {product.noseDescription ||
                  'Stone fruits such as cherry give way to forest fruits that subtly lift the fresh sensation. As it develops, the cassis embraces notes of pastry, giving an unexpected lightness to the sweetness.'}
              </Text>
              <Text style={{ marginBottom: 2 }}>
                <Text style={{ fontWeight: 500 }}>On the Palate:</Text>{' '}
                {product.palateDescription ||
                  'Stone fruits such as cherry give way to forest fruits that subtly lift the fresh sensation. As it develops, the cassis embraces notes of pastry, giving an unexpected lightness to the sweetness.'}
              </Text>
            </Box>
            <SemiTitle style={{ marginTop: 12 }}>
              Serving recommendations
            </SemiTitle>
            <Box
              style={{
                // Should this not have the same margin as the previous section?
                marginTop: 2,
              }}
            >
              <Text style={{ marginBottom: 2 }}>
                <Text style={{ fontWeight: 500 }}>Serving Temperature:</Text>{' '}
                {product.minTemp || '24'}°- {product.maxTemp || '42'}°C
              </Text>
              <Text style={{ marginBottom: 2 }}>
                <Text style={{ fontWeight: 500 }}>Serving Tips: </Text>
                {product.pairingDescription ||
                  'Ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom lorem ipsom.'}
              </Text>
            </Box>
            <SemiTitle style={{ marginTop: 12 }}>Highlights</SemiTitle>
            <Box
              style={{
                // Should this not have the same margin as the previous section?
                marginTop: 2,
              }}
            >
              <Text style={{ marginBottom: 2 }}>
                {product.custom_highlights?.value || 'Ipsom lorem ipsom'}
              </Text>
            </Box>
            <Spacer />

            <Columns
              style={{
                justifyContent: 'space-between',
                flexGrow: 0,
              }}
            >
              <Column style={{ width: '33%', marginRight: 10 }}>
                <Label variant="summary">Vineyard</Label>
                <SummaryItem label="Age of vines">
                  {ensureValue(product.custom_ageOfVines?.value)}
                </SummaryItem>
                <SummaryItem label="Yield">
                  {ensureValue(product.yield)}
                </SummaryItem>
                <SummaryItem label="Altitude">
                  {ensureValue(product.vineyardElevation)}
                </SummaryItem>
                <SummaryItem label="Climate">
                  {ensureValue(product.custom_climate?.value)}
                </SummaryItem>
                <SummaryItem label="Vineyards">
                  {ensureValue(product.custom_vineyards?.value)}
                </SummaryItem>
              </Column>
              <Column style={{ width: '33%', marginRight: 10 }}>
                <Label variant="summary">Vinification</Label>
                <SummaryItem label="Harvest">
                  {ensureValue(product.custom_harvest?.value)}
                </SummaryItem>
                <SummaryItem label="Vinification">
                  {ensureValue(product.custom_vinification?.value)}
                </SummaryItem>
                <SummaryItem label="Fermentation">
                  {ensureValue(product.custom_fermentation?.value)}
                </SummaryItem>
                <SummaryItem label="Maturation">
                  {ensureValue(product.custom_maturation?.value)}
                </SummaryItem>
              </Column>
              <Column style={{ width: '33%' }}>
                <Label variant="summary">Analytics</Label>
                <SummaryItem label="Alcohol">{product.alcohol} %</SummaryItem>
                <SummaryItem label="Total acidity">
                  {product.totalAcidity || 'XX'} g/l
                </SummaryItem>
                <SummaryItem label="pH">{ensureValue(product.pH)}</SummaryItem>
                <SummaryItem label="Residual Sugar">
                  {product.residualSugar || 'XX'} g/l
                </SummaryItem>
                <SummaryItem label="Volatile Acidity">
                  {ensureValue(product.custom_volatileAcidity?.value)}
                </SummaryItem>
              </Column>
            </Columns>
          </Column>
        </Columns>
      </Body>
      <Box style={{ height: 5, backgroundColor: 'black' }} />
      <Debug product={product} fixed />
    </Page>
  );
}

// This ensures that the client sees the fields for review.
// Can be removed after finalizing.
function ensureValue(value) {
  return value || 'Ipsum lorem';
}

function AwardItem({ award, medal }) {
  return (
    <Flex style={{ paddingHorizontal: 10, paddingVertical: 2 }}>
      <Image src={barcelonaAwardsLogo} style={{ height: 25, width: 25 }} />
      <Box style={{ marginLeft: 13, paddingTop: 7 }}>
        <SemiTitle style={{ textTransform: 'uppercase' }}>{medal}</SemiTitle>
        <Text variant="smaller" style={{ marginTop: 5 }}>
          {award}
        </Text>
      </Box>
    </Flex>
  );
}

function SummaryItem({ label, children }) {
  return (
    <Box style={{ textAlign: 'center', marginBottom: 8 }}>
      <Label>{label}</Label>
      <Text variant="smaller">{children}</Text>
    </Box>
  );
}

// We need to trick the IDE so we don't see errors for unknown fragments.
BarcelonaBrandsInfoSheetPage1.fragment = parse/* GraphQL */ `
  fragment BarcelonaBrandsInfoSheetInfoSheetPage1 on Product {
    productId
    shortName
    ... on Wine {
      classification
      subregion
      maxTemp
      minTemp
    }

    # Components
    ...Debug

    producer {
      producerId
      # Custom
      name

      # Components
      ...ProducerDescription
    }

    #companyProfile {
    # Components

    #}

    noseDescription
    appearanceDescription
    palateDescription

    # Components
    ...Alcohol
    ...Blend
    ...BottleImage
    ...BottleSize
    ...Country
    ...Origin
    ...PairingDescription
    ...PH
    ...ResidualSugar
    ...TotalAcidity
    ...Vintage
    ...VineyardElevation
    ... on Wine {
      customSubregion
      yield
      awards {
        award
        medal
      }
    }

    custom_ageOfVines: customFieldValue(key: "ageOfVines") {
      __typename
      ... on TextFieldValue {
        value
      }
    }

    custom_climate: customFieldValue(key: "climate") {
      __typename
      ... on TextFieldValue {
        value
      }
    }

    custom_vineyards: customFieldValue(key: "vineyards") {
      __typename
      ... on TextFieldValue {
        value
      }
    }

    custom_palletHeightFull: customFieldValue(key: "palletHeightFull") {
      __typename
      ... on TextFieldValue {
        value
      }
    }

    custom_volatileAcidity: customFieldValue(key: "volatileAcidityGl") {
      __typename
      ... on TextFieldValue {
        value
      }
    }

    custom_harvest: customFieldValue(key: "harvest") {
      __typename
      ... on TextFieldValue {
        value
      }
    }

    custom_vinification: customFieldValue(key: "vinification") {
      __typename
      ... on TextFieldValue {
        value
      }
    }

    custom_fermentation: customFieldValue(key: "fermentation") {
      __typename
      ... on TextFieldValue {
        value
      }
    }

    custom_maturation: customFieldValue(key: "maturation") {
      __typename
      ... on TextFieldValue {
        value
      }
    }

    custom_highlights: customFieldValue(key: "highlights") {
      __typename
      ... on TextFieldValue {
        value
      }
    }
  }

  ${Debug.fragment}
  ${Producer.ProducerDescription.fragment}
  ${Wine.Alcohol.fragment}
  ${Wine.Blend.fragment}
  ${Wine.BottleImage.fragment}
  ${Wine.BottleSize.fragment}
  ${Wine.Country.fragment}
  ${Wine.Origin.fragment}
  ${Wine.PairingDescription.fragment}
  ${Wine.PH.fragment}
  ${Wine.ResidualSugar.fragment}
  ${Wine.TotalAcidity.fragment}
  ${Wine.Vintage.fragment}
  ${Wine.VineyardElevation.fragment}
`;
